import { WebPageEvents } from '@griply/types';
import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullLogoDark, FullLogoLight } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { PaymentOptions, UspList } from 'shared/dialogs/purchase-premium';
import { useSubscriptionPrices } from 'shared/hooks/use-subscription-prices';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useBrowserPrefersDarkMode } from 'shared/lib/mui';
import { Price, PriceInterval } from 'shared/types/price';

import { BillingUsps } from './billing-usps';
import * as Styled from './premium.style';

const PREMIUM_IMAGE_URL = '/images/signup/illustration@3x.png';

const OnboardingRightPanelImage: React.FC<{ imageUrl: string }> = ({
  imageUrl,
}) => (
  <Styled.RightPanel>
    <Styled.RightPanelImage src={imageUrl} alt="App preview" />
  </Styled.RightPanel>
);

export type PremiumProps = {
  onSkip: () => void;
  isLoading: boolean;
  onSubmit: (price?: Price) => void;
  initialPriceSelection?: PriceInterval;
};

export const Premium: React.FC<PremiumProps> = ({
  onSkip,
  isLoading,
  onSubmit,
  initialPriceSelection = PriceInterval.Year,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingPremium, undefined);
  const { t } = useTranslation();
  const prefersDarkMode = useBrowserPrefersDarkMode();

  const { prices, discount } = useSubscriptionPrices();
  const [selectedPriceId, setSelectedPriceId] = useState(
    prices.find(({ interval }) => interval === initialPriceSelection)?.id ??
      prices[prices.length - 1]?.id,
  );

  const submitSelection = () => {
    const price = prices.find(({ id }) => id === selectedPriceId);
    onSubmit(price);
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <Styled.Header>
          {prefersDarkMode ? (
            <FullLogoDark height="24" width="85" />
          ) : (
            <FullLogoLight height="24" width="85" />
          )}
        </Styled.Header>

        <Styled.LeftPanelContent>
          <Styled.Form>
            <Styled.Content>
              <Typography variant="h4" component="h1">
                {t('dialogs.premium.title')}
              </Typography>
              <Styled.Subtitle>{t('dialogs.premium.slogan')}</Styled.Subtitle>
            </Styled.Content>

            <UspList twoColumns />

            <Styled.PaymentOptions>
              <PaymentOptions
                options={prices}
                discount={discount ?? undefined}
                onOption={setSelectedPriceId}
                selectedOption={selectedPriceId}
                disabled={isLoading}
              />
            </Styled.PaymentOptions>

            <Styled.MobileBillingUsps>
              <BillingUsps />
            </Styled.MobileBillingUsps>
          </Styled.Form>

          <Styled.FooterArea>
            <Styled.ButtonsContainer>
              <Button
                onClick={submitSelection}
                type="button"
                form="tasks-form"
                size={Sizes.Large}
                disabled={isLoading || !selectedPriceId}
                fullWidth
                alignCenter
                disabledBackgroundOnly
              >
                {isLoading ? (
                  <CircularProgress color="primary" size={20} />
                ) : (
                  t('dialogs.premium.interactions.confirm.trial')
                )}
              </Button>
              <Button
                type="button"
                onClick={onSkip}
                variant={Variants.Text}
                size={Sizes.Medium}
                color={Colors.Inherit}
                fullWidth
                alignCenter
              >
                {t('forms.onboarding.skip.label')}
              </Button>
            </Styled.ButtonsContainer>

            <Styled.DesktopBillingUsps>
              <BillingUsps />
            </Styled.DesktopBillingUsps>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>

      <Styled.RightPanelWrapper>
        <OnboardingRightPanelImage imageUrl={PREMIUM_IMAGE_URL} />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
